import { Toaster } from "sonner";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import Home from "@/pages/Home";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Tunnel from "@/pages/Tunnel";
import Admin from "@/pages/Admin";
import { EventProvider } from "@/components/EventScope";
import Gallery from "@/pages/Gallery";
import Pass from "@/pages/Pass";
import Termini from "@/pages/Termini";
import Privacy from "@/pages/Privacy";
import Rimborsi from "@/pages/Rimborsi";
import Contatti from "@/pages/Contatti";
import OffertaLancio from "@/pages/OffertaLancio";
import Voucher from "@/pages/Voucher";
import VoucherSuccess from "@/pages/VoucherSuccess";
import Riscatta from "@/pages/Riscatta";
import AggiungiMedia, { AggiungiMediaSuccess } from "@/pages/AggiungiMedia";
import ContabilitaPage from "@/pages/Contabilita";
import { PageTracker } from "@/components/PageTracker";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { FloatingWhatsApp } from "@/components/WhatsApp";
import { InstallPWA, InstallAppButton } from "@/components/InstallPWA";
import { publicMarketingConfig } from "@/lib/api";

// Inizializza dinamicamente Meta Pixel + verifica dominio Meta leggendo
// la config dal backend. Se l'admin cambia Pixel ID → attivo al prossimo reload.
function MetaConfigLoader() {
    useEffect(() => {
        let cancelled = false;
        publicMarketingConfig().then(cfg => {
            if (cancelled) return;
            const pid = (cfg?.pixel_id || "").trim();
            const dv = (cfg?.meta_domain_verification || "").trim();
            // 1) Meta Pixel init dinamico
            if (pid && typeof window !== "undefined" && window.fbq) {
                try {
                    window.fbq("init", pid);
                    window.fbq("track", "PageView");
                } catch (e) { /* no-op */ }
            }
            // 2) Domain verification meta-tag iniettato nell'<head>
            if (dv && typeof document !== "undefined") {
                const existing = document.querySelector('meta[name="facebook-domain-verification"]');
                if (existing) {
                    existing.setAttribute("content", dv);
                } else {
                    const m = document.createElement("meta");
                    m.setAttribute("name", "facebook-domain-verification");
                    m.setAttribute("content", dv);
                    document.head.appendChild(m);
                }
            }
        }).catch(() => { /* silent — pixel non installato o backend offline */ });
        return () => { cancelled = true; };
    }, []);
    return null;
}

function ConditionalWhatsApp() {
    const { pathname } = useLocation();
    // Nascondi su area admin, funnel post-pagamento, boarding pass e landing pubblicitaria
    if (pathname.startsWith("/admin") || pathname.startsWith("/manleva") || pathname.startsWith("/pass") || pathname.startsWith("/offerta-lancio") || pathname.startsWith("/voucher") || pathname.startsWith("/riscatta")) return null;
    return <FloatingWhatsApp />;
}

function ConditionalInstallPWA() {
    const { pathname } = useLocation();
    // Non mostrare il prompt sull'admin (workflow interno) o sul pass (contesto specifico)
    if (pathname.startsWith("/admin") || pathname.startsWith("/pass")) return null;
    return <InstallPWA />;
}

function ConditionalInstallChip() {
    const { pathname } = useLocation();
    // Chip permanente solo sulle pagine pubbliche di consumo cliente
    if (pathname.startsWith("/admin") || pathname.startsWith("/manleva")) return null;
    return <InstallAppButton variant="chip" />;
}

function App() {
    return (
        <div className="App">
            <LanguageProvider>
            <BrowserRouter>
            <EventProvider>
                <PageTracker />
                <MetaConfigLoader />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/gallery/:eventId" element={<Gallery />} />
                    <Route path="/prenota" element={<Navigate to="/#prodotti" replace />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/manleva/:bookingId" element={<Tunnel />} />
                    <Route path="/tunnel/:bookingId" element={<Tunnel />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/contabilita" element={<ContabilitaPage />} />
                    <Route path="/pass/:voucher" element={<Pass />} />
                    <Route path="/termini" element={<Termini />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/rimborsi" element={<Rimborsi />} />
                    <Route path="/contatti" element={<Contatti />} />
                    <Route path="/offerta-lancio" element={<OffertaLancio />} />
                    <Route path="/voucher" element={<Voucher />} />
                    <Route path="/voucher/success" element={<VoucherSuccess />} />
                    <Route path="/riscatta" element={<Riscatta />} />
                    <Route path="/aggiungi-media/success" element={<AggiungiMediaSuccess />} />
                    <Route path="/aggiungi-media/:id" element={<AggiungiMedia />} />
                </Routes>
                <ConditionalWhatsApp />
                <ConditionalInstallPWA />
                <ConditionalInstallChip />
            </EventProvider>
            </BrowserRouter>
            </LanguageProvider>
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#1C2026", color: "#F4F2ED", border: "1px solid rgba(154,162,173,0.2)", borderRadius: 0 } }} />
        </div>
    );
}

export default App;
